// =============================================================================
// Bridge to the iOS home-screen widget (WidgetKit).
//
// On native builds the app pushes a small JSON snapshot of "today" into the
// shared App Group container via the EuleaWidgetBridge Capacitor plugin
// (ios/App/App/EuleaWidgetBridge.swift); the SwiftUI widget renders it and
// the plugin asks WidgetKit to refresh. On the web this module is a no-op.
//
// The snapshot is deliberately tiny and display-ready: the widget process
// must not re-implement recurrence/priority logic.
// =============================================================================

import { Capacitor, registerPlugin } from '@capacitor/core';
import { format, addDays, startOfDay, startOfWeek, isSameMonth, differenceInCalendarDays } from 'date-fns';
import { expandEvents } from '../utils/recurrence';
import { defaultData } from '../utils/storage';
import { priorityRank, priorityEmoji } from './priorities';
import { getDateLocale, translate, weekdayLabels } from './i18n';
import { formatDate } from './dates';
import { currentTheme } from './theme';
import { austrianHolidaysBetween } from './holidays';
import { getLang } from './i18n';
import { myLabelFor } from './myLabels';

const Bridge = Capacitor.isNativePlatform()
  ? registerPlugin('EuleaWidgetBridge')
  : null;

const byTimeThenPriority = (a, b) => {
  const ta = a.time || '';
  const tb = b.time || '';
  if (ta !== tb) {
    if (!ta) return 1;
    if (!tb) return -1;
    return ta.localeCompare(tb);
  }
  return priorityRank(a.priority) - priorityRank(b.priority);
};

// ---------------------------------------------------------------------------
// Week grids per calendar, for the home-screen calendar widget. The widget
// draws exactly what it is given — month label, seven day cells and stacked
// event bars — so the layout logic (and therefore the look) stays here,
// beside the app's own calendar rules.
//
// We ship a *range* of weeks rather than just the current one: the widget
// process picks the week containing its own idea of "today". Otherwise the
// week (and the highlighted day) would freeze at whatever moment the app last
// pushed, and the widget would keep circling yesterday after midnight.
// ---------------------------------------------------------------------------
const WEEKS_BACK = 6;
const WEEKS_AHEAD = 6;

const colorsFor = (data, sharedId) => {
  const all = data.calendar?.categories || [];
  const scoped = all.filter((c) => (c.sharedCalendarId || null) === sharedId);
  const byId = new Map(scoped.map((c) => [c.id, c]));
  const base = defaultData.calendar.categories.map((d) => byId.get(d.id) || d);
  const map = new Map(base.map((c) => [c.id, c.color]));
  return (id) => map.get(id) || base[0].color;
};

// Every dated thing this calendar shows, as {title, start, end, color}, over
// the window the widget can display. Mirrors the app's own derivation so the
// widget and the calendar page can't disagree.
const itemsForCalendar = (data, sharedId, from, to) => {
  const color = colorsFor(data, sharedId);
  const scoped = (data.calendar?.events || []).filter(
    (ev) => (ev.sharedCalendarId || null) === sharedId
  );
  // Each item mirrors one calendar chip. `kind` and `allDay` decide the
  // paint exactly as Calendar.js does: all-day → solid colour with white
  // text; timed → the colour at 13% behind coloured text; to-dos → tinted
  // with a checkbox, struck through once done.
  const items = expandEvents(scoped, from, to).map((ev) => ({
    kind: 'event',
    title: ev.title || '',
    start: ev.date,
    end: ev.endDate || ev.date,
    allDay: ev.allDay !== false,
    time: ev.allDay !== false ? '' : ev.startTime || '',
    endTime: ev.allDay !== false ? '' : ev.endTime || '',
    color: color(ev.categoryId)
  }));

  // Birthdays, if this calendar links them in.
  const bs = sharedId
    ? (() => {
        const c = (data.calendar?.sharedCalendars || []).find((x) => x.id === sharedId);
        return c && c.hasBirthdays && c.birthdayLinked !== false
          ? { linked: true, categoryId: c.birthdayCategoryId || 'purple' }
          : { linked: false };
      })()
    : (() => {
        const b = data.calendar?.birthdaySettings || {};
        return { linked: !!b.linkedToCalendar, categoryId: b.categoryId || 'purple' };
      })();
  if (bs.linked) {
    const years = new Set([from.getFullYear(), to.getFullYear()]);
    for (const p of (data.calendar?.birthdays || []).filter(
      (b) => (b.sharedCalendarId || null) === sharedId
    )) {
      let day = p.day;
      let month = p.month;
      if (!day && p.birthDate) {
        const parts = String(p.birthDate).split('-').map(Number);
        month = parts[1];
        day = parts[2];
      }
      if (!day || !month) continue;
      // The window can straddle New Year, so emit the birthday for each year
      // it touches and let the range filter below drop the rest.
      for (const year of years) {
        const d = format(new Date(year, month - 1, day), 'yyyy-MM-dd');
        items.push({ kind: 'birthday', title: p.name, start: d, end: d, allDay: true, color: color(bs.categoryId) });
      }
    }
  }

  // Austrian public holidays, if this calendar switched them on.
  const hs = sharedId
    ? (() => {
        const c = (data.calendar?.sharedCalendars || []).find((x) => x.id === sharedId);
        return c ? { enabled: !!c.holidaysEnabled, categoryId: c.holidayCategoryId } : {};
      })()
    : data.calendar?.holidaySettings || {};
  if (hs.enabled) {
    for (const h of austrianHolidaysBetween(from.getFullYear(), to.getFullYear(), getLang())) {
      items.push({ kind: 'holiday', title: h.name, start: h.date, end: h.date, allDay: true, color: color(hs.categoryId || 'red') });
    }
  }

  // Linked to-dos.
  const todoSettings = data.todo?.calendarSettings || {};
  const todoSource = sharedId
    ? [...(data.todo?.tasks || []), ...(data.todo?.sharedTasks || [])].filter(
        (t) => t.sharedCalendarId === sharedId
      )
    : todoSettings.linkedToCalendar && !todoSettings.sharedCalendarId
    ? (data.todo?.tasks || []).filter((t) => !t.sharedCalendarId)
    : [];
  for (const t of todoSource) {
    if (!t.date) continue;
    items.push({
      kind: 'todo',
      title: t.title || '',
      start: t.date,
      end: t.date,
      allDay: true,
      done: !!t.done,
      emoji: priorityEmoji(t.priority),
      time: t.time || '',
      color: color(t.calendarCategoryId || todoSettings.categoryId || 'blue')
    });
  }

  return items;
};

// One week: seven day cells plus the event bars clipped to it and stacked into
// rows, longest first (the same rule the month grid uses for multi-day bars).
const buildWeek = (weekStart, items, locale) => {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const dayKeys = days.map((d) => format(d, 'yyyy-MM-dd'));
  const first = dayKeys[0];
  const last = dayKeys[6];

  // The month a week "belongs to" is the one holding most of its days, which
  // is the same as the month of its Thursday.
  const anchor = days[3];

  const inWeek = items.filter((it) => it.start <= last && it.end >= first);
  const col = (iso) => Math.max(0, Math.min(6, dayKeys.indexOf(iso < first ? first : iso > last ? last : iso)));

  // Multi-day all-day events span columns (Calendar.js getMultiDayBars):
  // longest first, packed into rows.
  const spans = inWeek
    .filter((it) => it.kind === 'event' && it.allDay && it.end !== it.start)
    .map((it) => ({
      kind: 'span',
      title: it.title,
      color: it.color,
      solid: true,
      allDay: true,
      startCol: col(it.start),
      endCol: col(it.end),
      continuesLeft: it.start < first,
      continuesRight: it.end > last
    }))
    .sort((a, b) => {
      const len = b.endCol - b.startCol - (a.endCol - a.startCol);
      return len !== 0 ? len : a.startCol - b.startCol;
    });
  const rowEnds = [];
  for (const bar of spans) {
    let row = 0;
    while (rowEnds[row] !== undefined && rowEnds[row] >= bar.startCol) row++;
    rowEnds[row] = bar.endCol;
    bar.row = row;
  }

  // Everything else sits in its own day, below whatever spans cross that
  // column, in the calendar's order (all-day first, then by start time —
  // the expansion already yields them in event order).
  const placed = [...spans];
  const birthdaysByDay = dayKeys.map(() => []);
  for (let c = 0; c < 7; c++) {
    const key = dayKeys[c];
    const spanRows = spans.filter((b) => b.startCol <= c && b.endCol >= c).length
      ? Math.max(...spans.filter((b) => b.startCol <= c && b.endCol >= c).map((b) => b.row)) + 1
      : 0;
    let row = spanRows;
    // The calendar orders a day's chips by start time, untimed ones first
    // (getEventsForDay in Calendar.js); keep exactly that order.
    const todays = inWeek
      .filter((it) => it.start <= key && it.end >= key)
      .sort((a, b) => (a.time || '').localeCompare(b.time || ''));
    for (const it of todays) {
      if (it.kind === 'event' && it.allDay && it.end !== it.start) continue; // a span
      if (it.kind === 'birthday') {
        birthdaysByDay[c].push({ title: it.title, color: it.color });
        continue;
      }
      placed.push({
        kind: it.kind,
        title: it.title,
        color: it.color,
        solid: it.kind !== 'todo' && it.allDay,
        allDay: it.allDay,
        done: !!it.done,
        emoji: it.emoji || '',
        startCol: c,
        endCol: c,
        row: row++,
        continuesLeft: false,
        continuesRight: false
      });
    }
  }

  return {
    start: first,
    end: last,
    month: format(anchor, 'MMMM', { locale }),
    monthKey: format(anchor, 'yyyy-MM'),
    days: days.map((d, i) => ({
      key: dayKeys[i],
      label: format(d, 'd'),
      weekend: d.getDay() === 0 || d.getDay() === 6,
      inMonth: isSameMonth(d, anchor),
      birthdays: birthdaysByDay[i]
    })),
    bars: placed
  };
};

const weeksForCalendar = (data, cal, now) => {
  const sharedId = cal.id === 'personal' ? null : cal.id;
  const thisWeek = startOfWeek(now, { weekStartsOn: 1 });
  const from = addDays(thisWeek, -7 * WEEKS_BACK);
  const to = addDays(thisWeek, 7 * WEEKS_AHEAD + 6);
  const items = itemsForCalendar(data, sharedId, from, to);
  const locale = getDateLocale();

  const weeks = [];
  for (let i = -WEEKS_BACK; i <= WEEKS_AHEAD; i++) {
    weeks.push(buildWeek(addDays(thisWeek, i * 7), items, locale));
  }
  return { id: cal.id, name: cal.name, weeks };
};

const calendarsForWidget = (data) => {
  const enabled = data.settings?.enabledFeatures || {};
  const out = [];
  if (enabled.calendar !== false && data.settings?.showPersonalCalendar !== false) {
    out.push({ id: 'personal', name: translate('todo.personalCalendar') });
  }
  for (const c of data.calendar?.sharedCalendars || []) {
    if (enabled[`shared:${c.id}`] === false) continue;
    out.push({ id: c.id, name: c.name });
  }
  return out;
};

// ---------------------------------------------------------------------------
// Per-domain widget data. These mirror the dashboard tiles (src/components/
// dashboard/bodies/*) so a widget and the in-app tile never disagree.
// ---------------------------------------------------------------------------
const WIDGET_ROWS = 12;

const relativeDayLabel = (isoDate, now) => {
  if (!isoDate) return '';
  const days = differenceInCalendarDays(new Date(`${isoDate}T00:00:00`), startOfDay(now));
  if (days === 0) return translate('common.today', 'Heute');
  if (days === 1) return translate('common.tomorrow', 'Morgen');
  return formatDate(isoDate, 'short');
};

// Open tasks, the ones due soonest first (undated last), ties by priority.
// Carries the id so the widget's checkbox can name what it completed.
const openTodosForWidget = (data, now) => {
  const own = (data.todo?.tasks || []).map((t) => ({ ...t, shared: false }));
  const shared = (data.todo?.sharedTasks || []).map((t) => ({ ...t, shared: true }));
  return [...own, ...shared]
    .filter((t) => !t.done && t.id)
    .sort((a, b) => {
      const da = a.date || '9999';
      const db = b.date || '9999';
      if (da !== db) return da.localeCompare(db);
      return byTimeThenPriority(a, b);
    })
    .slice(0, WIDGET_ROWS)
    .map((t) => ({
      id: String(t.id),
      title: t.title || '',
      time: t.time || '',
      date: t.date || null,
      dateLabel: relativeDayLabel(t.date, now),
      emoji: priorityEmoji(t.priority),
      shared: t.shared
    }));
};

// Next birthdays, the way the dashboard's "Geburtstage" tile derives them.
const birthdaysForWidget = (data, now) => {
  const anchor = startOfDay(now);
  const out = [];
  for (const p of data.calendar?.birthdays || []) {
    let { day, month, year } = p;
    if (!day && p.birthDate) {
      const parts = String(p.birthDate).split('-').map(Number);
      [year, month, day] = parts;
    }
    if (!day || !month) continue;
    let next = new Date(anchor.getFullYear(), month - 1, day);
    if (next < anchor) next = new Date(anchor.getFullYear() + 1, month - 1, day);
    out.push({
      id: String(p.id),
      name: p.name || '',
      dateLabel: formatDate(next, 'dayMonthLong'),
      days: differenceInCalendarDays(next, anchor),
      turns: year ? next.getFullYear() - year : null
    });
  }
  // The same person can be entered in the personal list and again in a
  // shared calendar; one row per (name, day) is what the widget wants.
  const seen = new Set();
  return out
    .sort((a, b) => a.days - b.days)
    .filter((b) => {
      const key = `${b.name.trim().toLowerCase()}|${b.dateLabel}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 8);
};

const eur = (n) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

// This month's totals, as the Budget page's balance card computes them.
const budgetForWidget = (data, now) => {
  const key = format(now, 'yyyy-MM');
  const sum = (list) =>
    (list || [])
      .filter((it) => it && String(it.date || '').slice(0, 7) === key)
      .reduce((acc, it) => acc + (Number(it.amount) || 0), 0);
  const income = sum(data.budget?.income);
  const expenses = sum(data.budget?.expenses);
  const savings = sum(data.budget?.savings);
  const remaining = income - expenses - savings;
  return {
    month: formatDate(now, 'month'),
    income,
    expenses,
    remaining,
    ratio: income > 0 ? Math.min(1, Math.max(0, (expenses + savings) / income)) : 0,
    incomeLabel: eur(income),
    expensesLabel: eur(expenses),
    remainingLabel: eur(remaining)
  };
};

// The running time-tracker entry, if any. The widget counts up from
// `startedAt` itself, so nothing here goes stale between refreshes.
export const timerForWidget = (data) => {
  const tt = data.timetracker || {};
  const running = (tt.entries || []).find((e) => e && e.startedAt && !e.endedAt);
  if (!running) return null;
  const company = (tt.companies || []).find((c) => c.id === running.companyId);
  return {
    id: String(running.id),
    startedAt: running.startedAt,
    label: (company && company.name) || running.title || ''
  };
};

// Unchecked shopping items, personal list first, with the amount as a short
// suffix ("2 kg"). Carries the id so the widget's checkbox can name the item.
const shoppingForWidget = (data) =>
  (data.shopping?.items || [])
    .filter((it) => it && !it.checked && it.id)
    .sort((a, b) => Number(!!a.sharedListId) - Number(!!b.sharedListId))
    .slice(0, WIDGET_ROWS)
    .map((it) => ({
      id: String(it.id),
      name: it.name || '',
      detail: [it.amount, it.unit].filter(Boolean).join(' '),
      shared: !!it.sharedListId
    }));

// Next events for the lock screen: everything real (no birthdays, to-dos or
// holidays) from now on, across the personal and shared calendars. When two
// events start at the same moment, only the ones carrying *my* label in
// their calendar are kept — unless none does, then all of them stay.
const LOCK_ROWS = 3;
const lockEventsForWidget = (data, now) => {
  const from = startOfDay(now);
  const nowKey = format(now, 'yyyy-MM-dd');
  const nowTime = format(now, 'HH:mm');
  const stillRelevant = (ev) => {
    const end = ev.endDate || ev.date;
    if (end < nowKey) return false;
    if (ev.date > nowKey || end > nowKey || ev.allDay) return true;
    // Today, timed: keep until it has ended (or, with no end, has started
    // within the last hour).
    if (ev.endTime) return ev.endTime >= nowTime;
    return !ev.startTime || ev.startTime >= nowTime || true;
  };
  const all = expandEvents(data.calendar?.events || [], from, addDays(from, 3))
    .filter(stillRelevant)
    .map((ev) => ({
      title: ev.title || '',
      date: ev.date,
      time: ev.allDay ? '' : ev.startTime || '',
      endTime: ev.allDay ? '' : ev.endTime || '',
      allDay: !!ev.allDay,
      calendarId: ev.sharedCalendarId || 'personal',
      categoryId: ev.categoryId || null
    }))
    .sort((a, b) => `${a.date} ${a.time || '00:00'}`.localeCompare(`${b.date} ${b.time || '00:00'}`));

  const slot = (ev) => `${ev.date}|${ev.time}`;
  const groups = new Map();
  for (const ev of all) {
    if (!groups.has(slot(ev))) groups.set(slot(ev), []);
    groups.get(slot(ev)).push(ev);
  }
  const isMine = (ev) => ev.calendarId !== 'personal' && ev.categoryId && ev.categoryId === myLabelFor(ev.calendarId);
  const out = [];
  for (const ev of all) {
    const g = groups.get(slot(ev));
    if (g.length > 1 && g.some(isMine) && !isMine(ev)) continue;
    out.push(ev);
  }
  return out.slice(0, LOCK_ROWS).map((ev) => ({
    title: ev.title,
    date: ev.date,
    calendarId: ev.calendarId,
    dayLabel: relativeDayLabel(ev.date, now),
    timeLabel: ev.allDay
      ? translate('widget.allDay', 'ganztägig')
      : `${ev.time}${ev.endTime ? '–' + ev.endTime : ''}`
  }));
};

// ---------------------------------------------------------------------------
// The agenda widget ("Nächste Termine"): everything dated in the coming two
// weeks across the enabled calendars — events with recurrences expanded,
// plus birthdays and holidays where a calendar links them in — one row
// each, grouped by day. To-dos have their own widget and stay out. The
// widget re-labels "Heute"/"Morgen" against its own clock and drops days
// that have passed, so the rows carry the day key as well as the label.
// ---------------------------------------------------------------------------
const AGENDA_DAYS = 14;
const AGENDA_ROWS = 40;

const agendaDayLabel = (dayKey, now) => {
  const days = differenceInCalendarDays(new Date(`${dayKey}T00:00:00`), startOfDay(now));
  if (days === 0) return translate('common.today', 'Heute');
  if (days === 1) return translate('common.tomorrow', 'Morgen');
  return formatDate(dayKey, 'weekdayShort');
};

const agendaForWidget = (data, now) => {
  const from = startOfDay(now);
  const to = addDays(from, AGENDA_DAYS);
  const todayKey = format(from, 'yyyy-MM-dd');
  const lastKey = format(to, 'yyyy-MM-dd');
  const nowTime = format(now, 'HH:mm');
  const rows = [];
  for (const cal of calendarsForWidget(data)) {
    const sharedId = cal.id === 'personal' ? null : cal.id;
    for (const it of itemsForCalendar(data, sharedId, from, to)) {
      if (it.kind === 'todo') continue;
      // A multi-day event shows once, on its first day still ahead.
      const dayKey = it.start < todayKey ? todayKey : it.start;
      if (dayKey > lastKey || it.end < todayKey) continue;
      // Today's timed events that are already over are not "upcoming".
      if (dayKey === todayKey && !it.allDay && it.endTime && it.endTime < nowTime) continue;
      rows.push({
        dayKey,
        dayLabel: agendaDayLabel(dayKey, now),
        title: it.title,
        timeLabel: it.allDay ? '' : `${it.time || ''}${it.endTime ? '–' + it.endTime : ''}`,
        color: it.color,
        solid: it.allDay,
        calendarId: cal.id,
        kind: it.kind
      });
    }
  }
  return rows
    .sort((a, b) => {
      if (a.dayKey !== b.dayKey) return a.dayKey.localeCompare(b.dayKey);
      // All-day first, then by start time — the calendar's order.
      if (!a.timeLabel !== !b.timeLabel) return a.timeLabel ? 1 : -1;
      return a.timeLabel.localeCompare(b.timeLabel) || a.title.localeCompare(b.title);
    })
    .slice(0, AGENDA_ROWS);
};

// Derive today's events (all calendars incl. shared) + due/overdue open tasks.
export const buildWidgetSnapshot = (data) => {
  const now = new Date();
  const todayStr = format(now, 'yyyy-MM-dd');

  const events = expandEvents(
    data.calendar?.events || [],
    startOfDay(now),
    addDays(startOfDay(now), 1)
  )
    .filter((ev) => {
      const end = ev.endDate || ev.date;
      return ev.date <= todayStr && end >= todayStr;
    })
    .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''))
    .slice(0, 12) // the large "Heute" widget shows up to 12 rows
    .map((ev) => ({ title: ev.title || '', time: ev.startTime || '' }));

  const openTasks = (data.todo?.tasks || []).filter((t) => !t.done);
  const dueToday = openTasks
    .filter((t) => t.date && t.date <= todayStr)
    .sort(byTimeThenPriority)
    .slice(0, 12) // the large "Heute" widget shows up to 12 rows
    .map((t) => ({
      title: t.title || '',
      time: t.time || '',
      emoji: priorityEmoji(t.priority)
    }));

  return {
    updatedAt: now.toISOString(),
    todayEvents: events,
    todos: dueToday,
    openCount: openTasks.length,
    weekdays: weekdayLabels(),
    calendars: calendarsForWidget(data).map((cal) => weeksForCalendar(data, cal, now)),
    // The per-domain widgets ("To-dos", "Geburtstage", "Budget", "Timer",
    // "Einkaufsliste"). Each is display-ready; the widget only lays it out.
    todosOpen: openTodosForWidget(data, now),
    birthdays: birthdaysForWidget(data, now),
    budget: budgetForWidget(data, now),
    timer: timerForWidget(data),
    shopping: shoppingForWidget(data),
    lockEvents: lockEventsForWidget(data, now),
    agenda: agendaForWidget(data, now),
    // The widget process has no access to the app's dictionary, so the few
    // strings it renders itself travel with the data.
    strings: {
      empty: translate('widget.openApp', 'Eulea öffnen, um den Kalender zu laden.'),
      todos: translate('widget.todos', 'To-dos'),
      allDone: translate('widget.allDone', 'Alles erledigt'),
      open: translate('widget.open', 'offen'),
      birthdays: translate('widget.birthdays', 'Geburtstage'),
      noBirthdays: translate('widget.noBirthdays', 'Keine Geburtstage'),
      budget: translate('widget.budget', 'Budget'),
      income: translate('widget.income', 'Einnahmen'),
      expenses: translate('widget.expenses', 'Ausgaben'),
      remaining: translate('widget.remaining', 'Übrig'),
      timer: translate('widget.timer', 'Zeiterfassung'),
      timerIdle: translate('widget.timerIdle', 'Kein Timer läuft'),
      start: translate('widget.start', 'Start'),
      stop: translate('widget.stop', 'Stopp'),
      shopping: translate('widget.shopping', 'Einkaufsliste'),
      shoppingEmpty: translate('widget.shoppingEmpty', 'Nichts zu kaufen'),
      today: translate('common.today', 'Heute'),
      tomorrow: translate('common.tomorrow', 'Morgen'),
      nextUp: translate('widget.nextUp', 'Nächster Termin'),
      nothingUpcoming: translate('widget.nothingUpcoming', 'Nichts anstehend'),
      allDay: translate('widget.allDay', 'ganztägig'),
      agenda: translate('widget.agenda', 'Nächste Termine'),
      // What Siri answers (ios/App/App/AppShortcuts.swift, Shared/TimerIntents.swift).
      added: translate('widget.added', 'Hinzugefügt'),
      timerStarted: translate('widget.timerStarted', 'Timer läuft.'),
      timerStopped: translate('widget.timerStopped', 'Timer gestoppt.')
    },
    // Dark mode is an in-app setting, so the widget has to be told which way
    // to paint rather than following iOS. `accent` is the palette the user
    // picked, so today's circle matches the app.
    theme: currentTheme()
  };
};

let lastJson = null;
let timer = null;

const writeSnapshot = async (data) => {
  try {
    const json = JSON.stringify(buildWidgetSnapshot(data));
    if (json === lastJson) return;
    lastJson = json;
    await Bridge.setSnapshot({ json });
  } catch {
    // Widget updates are best-effort; never disturb the app.
  }
};

// Debounced push — called from App.js whenever `data` changes. `immediate`
// skips the debounce for the few seconds a background push gives us.
export const pushWidgetSnapshot = (data, { immediate = false } = {}) => {
  if (!Bridge || !data) return Promise.resolve();
  if (timer) clearTimeout(timer);
  if (immediate) return writeSnapshot(data);
  timer = setTimeout(() => writeSnapshot(data), 800);
  return Promise.resolve();
};

// ---------------------------------------------------------------------------
// Silent push → refresh. The server sends a background push on every change
// someone else makes (supabase/silent_refresh.sql); AppDelegate hands it to
// us as `widgetRefresh`, we reload, rewrite the snapshot and say we are done
// so iOS can put the app back to sleep. No-ops on the web.
// ---------------------------------------------------------------------------
export const onWidgetRefresh = (handler) => {
  if (!Bridge) return () => {};
  const sub = Bridge.addListener('widgetRefresh', (ev) => handler(ev || {}));
  return () => {
    Promise.resolve(sub).then((h) => h && h.remove && h.remove()).catch(() => {});
  };
};

export const widgetRefreshDone = async () => {
  if (!Bridge) return;
  try {
    await Bridge.refreshDone();
  } catch {
    // Older native builds have no such method; the 25 s fallback covers it.
  }
};

// Live Activity for the running timer (Dynamic Island / lock screen). Keyed
// by entry id so a re-render with the same timer is a no-op natively.
export const syncTimerActivity = async (data) => {
  if (!Bridge) return;
  try {
    await Bridge.syncTimerActivity({ timer: data ? timerForWidget(data) : null });
  } catch {
    // Not supported on this device/iOS version; the widget still works.
  }
};
